import React, { useState, useEffect, useCallback } from 'react';
import {
    CalendarWhite,
    SidebarAccordion,
    DayCard
} from '../components';
import { Skeleton } from '../components/ui/skeleton';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger
} from "src/components/ui/tabs";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction';
import { fetchLitCalMonth, fetchRelatedLiturgicalData, fetchLiturgicalEventsByDate } from '../utilities/api';
import useLiturgicalEvents from '../hooks/useLiturgicalEvents';
import { fetchCultureAndCustomsListData, mapCultureCustomsListData, mapPrayerRitesListData, mapCatecheticalCoursesListData, fetchPrayerRitesListData, fetchCatecheticalCourses } from '../utilities'
import { useRef } from 'react';

const dayCardData = {
    date: 'May 1, 2024',
    label: 'Today',
    title: 'Liturgical Day',
    category: 'Easter',
    description: 'Several sentences about this day / feast / fast / abstinence. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin quis lobortis metus, ut egestas sem. Fusce orci ex, mollis nec convallis in, malesuada eu enim.',
};

function PageCalendar() {
    const loadedMonthRef = useRef('');
    const [relatedData, setRelatedData] = useState([]);
    const [accordionCCItems, setAccordionCCItems] = useState([]);
    const [accordionPRItems, setAccordionPRItems] = useState([]);
    const [accordionLTFItems, setAccordionLTFItems] = useState([]);
    const [eventLookup, setEventLookup] = useState({});
    const [loadedMonth, setLoadedMonth] = useState('');
    const calendarRef = useRef(null);
    const selectedDateRef = useRef(null);
    const [activeTab, setActiveTab] = useState('All');
    const [selectedDate, setSelectedDate] = useState(null)
    const [selectedDateKey, setSelectedDateKey] = useState(null);
    const [isAccordionVisible, setIsAccordionVisible] = useState(false);
    const [apiEvents, setApiEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    
    // const [eventData, setEventData] = useState(null);
    const [isLoading, setIsLoading] = useState(true)
    useEffect(() => {
        console.log("Selected date changed:", selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        console.log(' eventLookup updated:', eventLookup);
    }, [eventLookup]);



    const today = new Date();
    const { isDataLoading, eventData } = useLiturgicalEvents(
        today.getFullYear(),
        today.getMonth() + 1,
        today.getDate()
    );

    // Extract relevant event details
    const firstEventKey = eventData ? Object.keys(eventData)[0] : null;
    const firstEvent = firstEventKey ? eventData[firstEventKey] : null;

    // Ensure no flickering or static data issues
    const dayData = isDataLoading
        ? {} //Show skeleton while loading (NO STATIC DATA)
        : {
            date: firstEvent?.date
                ? new Date(firstEvent.date * 1000).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                })
                : "",
            title: firstEvent?.name
                ? firstEvent.name.replace(/\s*\/\/\s*/g, "  ")
                : "",
            tag: firstEvent?.liturgicalSeasonLcl || "",
            label: firstEvent ? "Today" : "",
            category: "Easter",
            description: firstEvent?.eventData?.description || '',
        };
    // Function to check if a date is the current date
    const isCurrentDate = useCallback((dateString) => {
        if (!dateString) return false;
        const eventDate = new Date(dateString);
        return (
            eventDate.getDate() === currentDate.getDate() &&
            eventDate.getMonth() === currentDate.getMonth() &&
            eventDate.getFullYear() === currentDate.getFullYear()
        );
    }, [currentDate]);

    const fetchMonthData = useCallback(async (year, month , isInitial = false) => {
        if(isInitial) setLoading(true)
        // setLoading(true);
        try {
            const rawData = await fetchLitCalMonth(year, month);
            const events = Object.values(rawData);
            const lookup = {};
            const formatted = events.map(event => {
                const isoDate = new Date(event.date * 1000).toISOString().split('T')[0];
                if (!lookup[isoDate]) lookup[isoDate] = [];
                lookup[isoDate].push(event);
                return {
                    title: event.name,
                    date: isoDate,
                    classNames: [event.eventData?.categoryClass || 'bda-normal-event']
                };
            });
            console.log("Formatted Api Events ", formatted.map(ev => ev.date));

            setEventLookup(lookup);
            setApiEvents(formatted);
            setSelectedDate(`${selectedDate || 'none'}-${Date.now()}`);
        } catch (err) {
            console.error("Error loading liturgical events:", err);
            setEventLookup({});
            setApiEvents([]);
        } finally {
           if(isInitial) setLoading(false);
        }
    }, []);

    useEffect(() => {
        const now = new Date();
        const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        loadedMonthRef.current = key;
        fetchMonthData(now.getFullYear(), now.getMonth() + 1 , true);
    }, [fetchMonthData]);

    useEffect(() => {
        const loadCurrentDayData = async () => {
            setIsLoading(true);
            try {
                const today = new Date();
                const year = today.getFullYear();
                const month = today.getMonth() + 1;
                const day = today.getDate();
                const eventData = await fetchLiturgicalEventsByDate(year, month, day);
                const firstKey = Object.keys(eventData)?.[0];
                const postId = eventData?.[firstKey]?.eventData?.post?.ID;

                if (!postId) {
                    setIsLoading(false)
                    return;
                };

                const related = await fetchRelatedLiturgicalData(postId);

                // Group the response
                const cc = [];
                const pr = [];
                const ltf = [];

                related.forEach(item => {
                    const type = item?.type;
                    const title = item?.attributes?.title || '';
                    const categories = item?.attributes?.ps_content_type || '';
                    const obj = { title, categories };

                    if (type === 'ps-culture-customs') cc.push(obj);
                    else if (type === 'ps-prayer-rites') pr.push(obj);
                    else if (type === 'ps-catechesis') ltf.push(obj);
                });

                setAccordionCCItems(cc);
                setAccordionPRItems(pr);
                setAccordionLTFItems(ltf);
            } catch (error) {
                console.error('Failed to load current date related data:', error);
            } finally {
                setIsLoading(false)
            }
        };

        loadCurrentDayData();
    }, []);

    const loadAccordionContentByDate = useCallback(async (dateStr) => {
        setIsLoading(true);
        console.log("Loading started ===>>>");

        let eventsForDay = eventLookup[dateStr];
        let postId = eventsForDay?.[0]?.eventData?.post?.ID;

        if (!postId) {
            const dateObj = new Date(dateStr);
            const fallbackData = await fetchLiturgicalEventsByDate(
                dateObj.getFullYear(),
                dateObj.getMonth() + 1,
                dateObj.getDate()
            );
            const firstKey = Object.keys(fallbackData)?.[0];
            postId = fallbackData?.[firstKey]?.eventData?.post?.ID;
        }

        if (!postId) {
            setAccordionCCItems([]);
            setAccordionPRItems([]);
            setAccordionLTFItems([]);
            setIsLoading(false);
            return;
        }

        try {
            const related = await fetchRelatedLiturgicalData(postId);
            const ccItems = [], prItems = [], ltfItems = [];

            related.forEach(item => {
                const obj = {
                    title: item?.attributes?.title || '',
                    categories: item?.attributes?.ps_content_type || ''
                };
                if (item.type === 'ps-culture-customs') ccItems.push(obj);
                else if (item.type === 'ps-prayer-rites') prItems.push(obj);
                else if (item.type === 'ps-catechesis') ltfItems.push(obj);
            });

            setAccordionCCItems(ccItems);
            setAccordionPRItems(prItems);
            setAccordionLTFItems(ltfItems);
            setIsAccordionVisible(true);
        } catch (error) {
            console.error("Error loading accordion content:", error);
            setAccordionCCItems([]);
            setAccordionPRItems([]);
            setAccordionLTFItems([]);
        } finally {
            console.log("setIsLoading Ended");

            setIsLoading(false); // Always stop loading at the end
        }
    }, [eventLookup]);

    // Determine category class function
    const determineCategoryClass = (title) => {
        const lowerTitle = title.toLowerCase();

        if (lowerTitle.includes('martyr')) return ['bda-fast-event'];
        if (lowerTitle.includes('apostle')) return ['bda-feast-event'];
        if (lowerTitle.includes('bishop')) return ['bda-abstinence-event'];
        if (lowerTitle.includes('virgin')) return ['bda-feast-event'];
        if (lowerTitle.includes('confessor')) return ['bda-feast-event'];
        if (lowerTitle.includes('pope')) return ['bda-feast-event'];
        if (lowerTitle.includes('presentation')) return ['bda-feast-event'];

        return ['bda-normal-event'];
    };

    const getEventIconHTML = (className) => {
        if (className.includes('fast')) {
            return `<img src="/media/images/discipline_fast.svg" alt="Fast" class="w-5 h-5" />`;
        } else if (className.includes('abstinence')) {
            return `<img src="/media/images/discipline_abstain.svg" alt="Abstinence" class="w-5 h-5" />`;
        } else if (className.includes('feast') || className.includes('holyday')) {
            return `<img src="/media/images/discipline_holyday.svg" alt="Feast / Holyday" class="w-5 h-5" />`;
        } else {
            return `<svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8.5" cy="8.5" r="7" stroke="#876B2E" stroke-width="1.5" />
          </svg>`;
        }
    };

    // Update getEventIcon to be more robust
    const getEventIcon = (classNames) => {
        // Ensure classNames is an array and convert to lowercase string
        const classString = (Array.isArray(classNames) ? classNames : [classNames])
            .join(' ')
            .toLowerCase();

        if (classString.includes('fast')) {
            return <img src={`${process.env.PUBLIC_URL}/media/images/discipline_fast.svg`} alt="Fast" className="w-6 h-6" />;
        } else if (classString.includes('abstinence')) {
            return <img src={`${process.env.PUBLIC_URL}/media/images/discipline_abstain.svg`} alt="Abstinence" className="w-6 h-6" />;
        } else if (classString.includes('feast') || classString.includes('holyday')) {
            return <img src={`${process.env.PUBLIC_URL}/media/images/discipline_holyday.svg`} alt="Feast / Holyday" className="w-6 h-6" />;
        } else {
            return

        }
    };

    const handleEventClick = async (info) => {
        setIsLoading(true);
        try {

            console.log('Event clicked:', info.event);
            const clickedDate = info.event.startStr;
            console.log('[eventClick] Date clicked:', clickedDate);
            setSelectedDate(clickedDate);
            setIsAccordionVisible(true);
            setIsLoading(true);
            console.log('Fetching accordion content for date:', clickedDate);

            let postId = null;

            // 1. Try getting from eventLookup
            const eventsForDay = eventLookup[clickedDate];
            console.log("Events for day:", eventsForDay);

            if (eventsForDay?.length > 0) {
                postId = eventsForDay[0]?.eventData?.post?.ID;
            }

            // 2. Fallback if no post found in lookup
            if (!postId) {
                const dateObj = new Date(clickedDate);
                const year = dateObj.getFullYear();
                const month = dateObj.getMonth() + 1;
                const day = dateObj.getDate();
                console.log('Fallback: fetching by date from API...');
                const fallbackData = await fetchLiturgicalEventsByDate(year, month, day);
                const firstKey = Object.keys(fallbackData)[0];
                postId = fallbackData?.[firstKey]?.eventData?.post?.ID;
            }

            if (!postId) {
                console.warn("No post ID found for date:", clickedDate);
                setAccordionCCItems([]);
                setAccordionPRItems([]);
                setAccordionLTFItems([]);
                return;
            }

            // 3. Fetch related content
            const related = await fetchRelatedLiturgicalData(postId);
            console.log('Fetched related items:', related);

            // 4. Group by type
            const cc = [], pr = [], ltf = [];
            related.forEach(item => {
                const type = item?.type;
                const title = item?.attributes?.title || '';
                const categories = item?.attributes?.ps_content_type || '';
                const obj = { title, categories };
                if (type === 'ps-culture-customs') cc.push(obj);
                else if (type === 'ps-prayer-rites') pr.push(obj);
                else if (type === 'ps-catechesis') ltf.push(obj);
            });

            setAccordionCCItems(cc);
            setAccordionPRItems(pr);
            setAccordionLTFItems(ltf);
        } catch (err) {
            console.error('Error in handleEventClick:', err);
            setAccordionCCItems([]);
            setAccordionPRItems([]);
            setAccordionLTFItems([]);
        } finally {
            setIsLoading(false);
        }
    };

    const allEvents = [...apiEvents];
    // Robust filtering function
    const filteredEvents = useCallback(() => {
        const allEvents = [...apiEvents];

        if (activeTab === "All") return allEvents;

        return allEvents.filter(event => {
            // Safely handle events with potential undefined properties
            const title = (event.title || '').toLowerCase();
            const classNames = event.classNames || [];

            // Check if any class matches the active tab
            const tabLower = activeTab.toLowerCase();
            return classNames.some(cn => cn.includes(tabLower)) ||
                (tabLower === 'feast' && (
                    title.includes('virgin') ||
                    title.includes('feast') ||
                    title.includes('sunday') ||
                    title.includes('easter') ||
                    title.includes('pentecost')
                )) ||
                (tabLower === 'fast' && (
                    title.includes('martyr') ||
                    title.includes('fast') ||
                    title.includes('good friday')
                )) ||
                (tabLower === 'abstinence' && (
                    title.includes('bishop') ||
                    title.includes('abstinence') ||
                    title.includes('friday') ||
                    title.includes('lent')
                ));
        });
    }, [activeTab, apiEvents,]);
    // Render function for events
    const renderEventContent = (eventInfo) => {
        // console.log('Event Info:', eventInfo);

        // Ensure we have class names
        const classNames = eventInfo.event.classNames
            ? eventInfo.event.classNames
            : ['bda-normal-event'];

        const icon = getEventIcon(classNames);

        return (
            <div className="flex items-center justify-center bda-calendar-event">
                <div className="flex flex-col items-center justify-center">
                    {/* <span className="mb-1 text-xs text-center text-gold-500">
                            {eventInfo.event.title}
                        </span> */}
                    <div className="w-5 h-5">
                        {icon}
                    </div>
                </div>
            </div>
        );
    };
    return (
        <>
            <div className="bg-white-50 max-w-full w-full lg:w-[60%] xl:w-[67.33%]">
                <div className="">
                    {/* 📱 Mobile Header (iOS-style) */}
                    <div className="flex items-center justify-between px-4 pt-6 pb-4 md:hidden relative border-b border-gray-100 bg-white-50 z-10">
                        {/* Left: Back Arrow Button (optional action) */}
                        <button className="text-gray-700">
                            <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                                <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {/* Center: Calendar Title */}
                        <h1 className="absolute left-1/2 transform -translate-x-1/2 text-xl font-cmr font-semibold text-gray-900">
                            Calendar
                        </h1>

                        {/* Right: Today Button */}
                        <button
                            onClick={() => calendarRef.current?.getApi().today()}
                            className="px-4 py-1.5 text-sm text-gray-700 border border-gray-200 rounded-full font-medium"
                        >
                            Today
                        </button>
                    </div>
                    <div className="hidden md:flex justify-between px-6 py-8 calendar-wrapper bg-white-50 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <span className="w-6 h-6 mt-px text-gold-200">
                                <CalendarWhite className="w-full h-full" />
                            </span>
                            <h1 className="text-2xl font-bold leading-7 text-gray-900 font-cmr">Calendar</h1>
                        </div>
                        <p className="text-xs leading-4 text-gray-500 text-normal py-1.3">
                            Season:
                            <span className="text-gold-500 ml-3 text-medium px-2 py-1.3 bg-gold-50 rounded-27">Easter</span>
                        </p>
                    </div>
                    <div className="hidden px-4 pb-8 xl:px-6 md:block bda-full-calendar">
                        {loading ? (
                            <Skeleton className="w-full h-96" />
                        ) : (
                            <FullCalendar
                                key={selectedDate}
                                ref={calendarRef}
                                selectable={true}
                                plugins={[dayGridPlugin, interactionPlugin]}
                                initialView='dayGridMonth'
                                initialDate={currentDate}
                                weekends={true}
                                events={loading ? [] : filteredEvents()}
                                eventContent={renderEventContent}
                                eventClick={handleEventClick}
                                // eventDisplay='block' // Ensure events are displayed
                                // default={false}
                                dayMaxEvents={true}
                                headerToolbar={{
                                    left: 'prev',
                                    center: 'title',
                                    right: 'next today',
                                }}
                                buttonText={{
                                    today: 'Today',
                                }}
                                height="auto"
                                contentHeight="auto"
                                dateClick={async (info) => {
                                    const clickedDate = info.dateStr;
                                    console.log('[dateClick] Date clicked:', clickedDate);
                                    const todayStr = new Date().toISOString().split('T')[0];
                                    const calendarApi = calendarRef.current?.getApi();
                                    // if (clickedDate === todayStr) {
                                    //     console.log('[dateClick] Today clicked - ignoring');
                                    //     return;
                                    // }
                                    // Toggle logic
                                    if (selectedDateRef.current === clickedDate) {
                                        console.log("Date already selected ");

                                        // Already selected → deselect
                                        selectedDateRef.current = null;
                                        setSelectedDate(null);
                                        setIsAccordionVisible(false);
                                        console.log('Deselected:', clickedDate);
                                        setIsLoading(false)
                                    } else {
                                        console.log("New Date selected");

                                        // New date selected
                                        selectedDateRef.current = clickedDate;
                                        setSelectedDate(clickedDate);
                                        console.log('Selected date changed:', clickedDate);
                                        await loadAccordionContentByDate(clickedDate);
                                    }
                                    // calendarRef.current?.getApi().render();

                                    if (calendarApi) {
                                        calendarApi.render();
                                      }                                }}
                                dayCellDidMount={(info) => {
                                    const frame = info.el.querySelector('.fc-daygrid-day-frame');
                                    if (!frame) return;
                                    const cellDate = info.date.toISOString().split('T')[0];
                                    if(frame.dataset.rendered === cellDate) return;
                                    // Clear previous icon container (if re-rendered)
                                    // const oldIcon = frame.querySelector('.event-icon-wrapper');
                                    // if (oldIcon) oldIcon.remove();
                                    const eventsForDay = eventLookup[cellDate];
                                    if (eventsForDay && eventsForDay.length > 0) {
                                        const wrapper = document.createElement('div');
                                        wrapper.className = 'event-icon-wrapper mt-[-20px] flex justify-center ml-[43px]';
                                        const iconType = determineCategoryClass(eventsForDay[0].name)?.[0] || '';
                                        const iconHTML = getEventIconHTML(iconType);
                                        wrapper.innerHTML = iconHTML;
                                        frame.appendChild(wrapper);
                                        frame.dataset.rendered = cellDate;
                                    }
                                    // Highlight selected date
                                    const selected = selectedDateRef.current;
                                    if (cellDate === selected) {
                                        frame.classList.add('bda-active');
                                    } else {
                                        frame.classList.remove('bda-active');
                                    }
                                }}
                                datesSet={(arg) => {
                                    console.log('visible range ' , arg.startStr , arg.endStr);
                                    const newDate = new Date(arg.view.currentStart); // use view's currentStart
                                    const newMonthKey = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
                                    console.log("LoadedMonth:", loadedMonthRef.current, "New:", newMonthKey);
                                    setCurrentDate(newDate)
                                    if (loadedMonthRef.current !== newMonthKey) {
                                        console.log("New month detected");
                                        loadedMonthRef.current = newMonthKey;
                                        fetchMonthData(newDate.getFullYear(), newDate.getMonth() + 1);
                                    } else {
                                        console.log('Same Month - Skipping Fetch');
                                    }
                                }}
                            />
                        )}
                    </div>
                    <div className="block md:hidden">
                        <Tabs defaultValue="All" className="flex flex-col items-center justify-center ">
                            <TabsList className="p-1 bg-gold-50 week-calender-tab-header">
                                {["All", "Feast", "Abstinence", "Fast"].map(tab => (
                                    <TabsTrigger
                                        key={tab}
                                        value={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`p-0 text-sm	font-medium tab-name-${tab.toLowerCase()} ${activeTab === tab ? "active" : ""}`}
                                    >
                                        {tab}
                                    </TabsTrigger>
                                ))}
                            </TabsList>

                            {["All", "Feast", "Abstinence", "Fast"].map(tab => (
                                <TabsContent key={tab} value={tab} className="overflow-hidden">
                                    <div className="bda-full-calendar bda-compact-calendar">
                                        <FullCalendar
                                            plugins={[multiMonthPlugin]}
                                            initialView="multiMonthFourMonth"
                                            weekends={true}
                                            headerToolbar={false}
                                            // eventDisplay='block'
                                            events={loading ? [] : filteredEvents()}
                                            eventContent={renderEventContent}
                                            views={{
                                                multiMonthFourMonth: {
                                                    type: "multiMonth",
                                                    duration: { months: 2 }
                                                }
                                            }}
                                            eventClick={handleEventClick}
                                            height="auto"
                                            contentHeight="auto"
                                        />
                                        <div className="pl-4 py-4 bg-gold-50 fixed bottom-[88px] left-0 right-0 w-full z-5 md:hidden block">
                                            <p className="text-xs font-normal leading-4 text-gray-500">Season:<span className="px-2 py-1 mt-4 ml-1 font-medium bg-white-50 text-gold-500 rounded-27">Easter</span></p>
                                        </div>
                                    </div>
                                </TabsContent>
                            ))}
                        </Tabs>
                    </div>
                </div>
            </div>
            <div className={`lg:ml-0 md:h-auto bg-gold-50 max-w-full w-full lg:w-[40%] xl:w-[32.67%] transition-transform duration-500 ease-out transform md:block ${isAccordionVisible ? "block overflow-auto" : "hidden"}`}>
                <div className='flex justify-center pt-2 md:hidden'>
                    <button
                        onClick={() => setIsAccordionVisible(prev => !prev)}
                        className="h-1 text-gray-300 border-4 border-t rounded w-9"
                    ></button>
                </div>
                <div className="top-0 flex flex-col px-4 py-8 xl:px-6 md:sticky">
                    <DayCard data={dayData} isDataLoading={isDataLoading} />
                    <div className='pt-6 space-y-6'>
                        {isLoading ? (
                            <>
                                <Skeleton className="w-2/3 h-6 mb-2" />
                                <Skeleton className="w-full h-16 mb-4" />
                                <Skeleton className="w-full h-16 mb-4" />
                                <Skeleton className="w-2/3 h-6 mb-2" />
                                <Skeleton className="w-full h-16 mb-4" />
                            </>
                        ) : (
                            <>
                                {accordionCCItems.length === 0 &&
                                    accordionPRItems.length === 0 &&
                                    accordionLTFItems.length === 0 ? (
                                    <p className="text-sm text-gray-500">
                                        No related content found for this day.
                                    </p>
                                ) : (
                                    <>
                                        {accordionCCItems.length > 0 && (
                                            <SidebarAccordion
                                                data={{
                                                    items: accordionCCItems,
                                                    sectionDetails: { title: "Culture & Customs", Active: true },
                                                }}
                                                defaultOpen={true}
                                            />
                                        )}
                                        {accordionPRItems.length > 0 && (
                                            <SidebarAccordion
                                                data={{
                                                    items: accordionPRItems,
                                                    sectionDetails: { title: "Prayers & Rites" },
                                                }}
                                                defaultOpen={false}
                                            />
                                        )}
                                        {accordionLTFItems.length > 0 && (
                                            <SidebarAccordion
                                                data={{
                                                    items: accordionLTFItems,
                                                    sectionDetails: { title: "Learn the Faith" },
                                                }}
                                                defaultOpen={false}
                                            />
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
export default PageCalendar;